import { useState, useEffect, ChangeEvent, MouseEvent } from "react";
import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "src/app/hooks";
import { selectAuth, setAuthInfo } from "src/features/authSlice";
import { HttpMethod, callApi } from "src/services/apiService";
import { runtimeConfig } from "src/services/runtimeConfig";
import ThemeSelector from "./ThemeSelector";
import Cookies from "js-cookie";
import Styles from "./Header.module.css";
import ProfileDropdown from "./ProfileDropdown";
import SVGForPage from "./SVGForPage";
import Loading from "src/components/Loading";
import HelpButton from "./HelpButton";
import { IMenu, IMenuItem } from "src/interfaces/IHeader";
import { getLogo } from "src/services/logoService";
import { useLocation } from "react-router-dom";
import { useUploadListContext } from "src/contexts/UploadListContext";
import { ITreeNode } from "src/interfaces/ITreeNode";


type DropdownItem = {
  label: string;
  href: string;
  isActive?: boolean;
  // Explicit icon keyword for SVGForPage (used when the URL's last segment isn't a
  // known icon keyword).
  iconKeyword?: string;
  external?: boolean; // when true, renders <a target="_blank"> instead of <Link>
  sysadminOnly?: boolean; // hidden from non-sysadmins (mirrors TrustCallLayout sidebar)
};

type NavDropdown = {
  label: string;
  caption: string;
  href?: string;
  items: DropdownItem[];
};

// Top-nav dropdown for the React TrustCall portal (/trustcall/*), shown to every
// user in place of the single backend-provided TrustCall link. Mirrors the
// TrustCall left-hand nav sections (TrustCallLayout), including which items are
// sysadmin-only. The deprecated MVC page stays reachable by direct URL only.
const NEW_TRUSTCALL_MENU: DropdownItem[] = [
  { label: "Dashboard", href: "/trustcall/dashboard", iconKeyword: "tc-dashboard" },
  { label: "Current Numbers", href: "/trustcall/phones", iconKeyword: "tc-numbers" },
  { label: "Add Numbers", href: "/trustcall/phones/add", iconKeyword: "tc-add" },
  { label: "Bulk Add", href: "/trustcall/phones/bulk-add", iconKeyword: "upload-list" },
  { label: "Bulk Remove", href: "/trustcall/phones/bulk-remove", iconKeyword: "tc-bulk-remove" },
  { label: "Archive", href: "/trustcall/archive", iconKeyword: "tc-archive" },
  { label: "Legal Entities", href: "/trustcall/settings/legal-entities", iconKeyword: "dnc-policy", sysadminOnly: true },
  { label: "Notifications", href: "/trustcall/settings/notifications", iconKeyword: "tc-notifications" },
  { label: "Webhooks", href: "/trustcall/settings/webhooks", iconKeyword: "tc-webhooks" },
  { label: "Sysadmin Tools", href: "/trustcall/settings/sysadmin", iconKeyword: "other-tools", sysadminOnly: true },
  { label: "User Guide", href: "/trustcall/help/user-guide", iconKeyword: "help-docs" },
  { label: "API Documentation", href: "https://docs.dncscrub.com/api-reference/trustcall/overview", iconKeyword: "tc-api-docs", external: true },
];

const underlineSectionIfActive = (section: string) => {
  const sectionToPathMap: Record<string, string> = {
    main: "/main",
    accounts: "/admin",
    trustcall: "/trustcall",
    users: "/t/users",
    reports: "/reports",
    logocall: "/logocall",
    "compliance guide": "/compliance-guide",
    "litigator research": "/t/litigator-research",
  };

  const path = sectionToPathMap[section.toLowerCase()];
  return window.location.pathname.startsWith(path)
    ? "underline-navbar-item"
    : "";
};

const Header: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isSysadmin, hasSalesReportAccess, acctId } =
    useAppSelector(selectAuth);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [newAcctId, setNewAcctId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [logo, setLogo] = useState("");
  const [isDefaultLogo, setIsDefaultLogo] = useState(false);
  const [filteredDropdowns, setFilteredDropdowns] = useState<NavDropdown[]>([]);
  const location = useLocation();
  const { setProjectId, setCampaignId, setTreeData } = useUploadListContext();

  const fetchMenuItems = async () => {
    try {
      const apiUrl = `Header`;
      const response = await callApi(apiUrl, HttpMethod.GET);
      if (!response) {
        console.info(
          "Failed to fetch header data. Likely user unauthenticated.",
        );
        return;
      }

      // Whitelabel accounts keep their own logo; everyone else gets "DNCScrub by Sonera".
      const fetchedLogo = getLogo(response.logo);
      setLogo(fetchedLogo ?? "");
      setIsDefaultLogo(!fetchedLogo);

      const updatedDropdowns: NavDropdown[] = [];

      for (const menuKey of Object.keys(response.menu)) {
        const menuItems = response.menu[menuKey as keyof IMenu];
        if (menuItems.length > 0) {
          updatedDropdowns.push({
            label: response.captions[menuKey] || menuKey,
            caption: response.captions[menuKey], // Fetch the caption using the menu key
            items: menuItems.map((item: IMenuItem) => ({
              label: item.key,
              href: `${item.value}`,
            })),
          });
        }
      }

      // Everyone gets the React TrustCall portal as a top-nav dropdown of all its
      // sections; sysadmin-only sections are filtered out for regular users.
      const trustCallItems = NEW_TRUSTCALL_MENU.filter(
        (item) => !item.sysadminOnly || isSysadmin,
      );
      const tc = updatedDropdowns.find(
        (d) => d.label?.toLowerCase() === "trustcall",
      );
      if (tc) {
        tc.items = trustCallItems;
      } else {
        updatedDropdowns.push({
          label: "TrustCall",
          caption: "TrustCall",
          items: trustCallItems,
        });
      }

      // Update the state to hold the filtered dropdown items
      setFilteredDropdowns(updatedDropdowns);
    } catch (error) {
      console.error("Failed to fetch and parse header data:", error);
    }
  };

  useEffect(() => {
    fetchMenuItems();
  }, [acctId, isSysadmin]);

  /* 
    Collapse dropdown menu if open and the user clicks outside
    the browser. Especially useful with the iframes we have. If we don't have
    this you can't click outside the iframe to close the dropdown.
    */
  useEffect(() => {
    const collapseDropdownMenuIfOpen = () => {
      const elements = document.querySelectorAll(".show.dropdown-menu");
      elements.forEach((element) => {
        element.classList.remove("show");
      });
    };
    window.addEventListener("blur", collapseDropdownMenuIfOpen);
    return () => {
      window.removeEventListener("blur", collapseDropdownMenuIfOpen);
    };
  }, []);

  const handleAcctChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNewAcctId(e.target.value);
  };

  const handleAcctIdChange = async () => {
    setIsLoading(true);
    try {
      const upperCaseAcctId = newAcctId.toUpperCase();
      const apiUrl = `Users/ChangeAccount?newAcctId=${upperCaseAcctId}`;
      const response = await callApi(apiUrl, HttpMethod.GET);
      if (response.type === "FAILED") {
        console.log("Failed to change account");
        navigate("/error", { state: { message: response.message } });
      } else {
        if (response.acctId === upperCaseAcctId) {
          // Below line needed to clear tree data or GetSessionRows API call intermittently
          // made with wrong campaignId immediately after acct change
          setTreeData([] as ITreeNode[]);

          setCampaignId("");
          setProjectId("");

          dispatch(
            setAuthInfo({
              acctId: response.acctId,
              acctName: response.acctName,
              username: response.username,
              fullName: response.fullName,
              quickScrub: response.quickScrub,
              role: response.role,
              isSysadmin: response.isSysadmin,
              hasSalesReportAccess: response.hasSalesReportAccess ?? false,
              loginId: response.loginId,
              csrfToken: response.csrfToken,
              hasTrainingFeature: response.hasTrainingFeature,
              homePage: response.homePage,
              landingPage: response.landingPage,
              acctMessage: response.acctMessage,
              userEmail: response.userEmail,
              isEmailVerified: response.isEmailVerified,
              isNXXG: response.isNXXG ?? false,
              expiresAt: response.expiresAt ?? null,
            }),
          );
          Cookies.set("acctId", upperCaseAcctId, {
            domain: runtimeConfig.cookieDomain,
            path: "/",
          });

          // New account = entirely new data context. Drop all cached React
          // Query data so pages refetch for the new account instead of
          // showing the previous account's stale responses. Most queries use
          // static, account-agnostic queryKeys (e.g.
          // ["trustcall","dashboard","service-type-summary"]), so without this
          // the cache serves the prior account's data after a switch.
          queryClient.clear();
          // Just let the server send the loginId cookie unless
          // on dev where the server may be at domain https://localhost.
          // See if this eliminates intermittent bug where user is
          // logged into React app but can't use Tcl app
          // - JH 2025-02-15
          const shouldSetLoginIdCookie =
            process.env.NODE_ENV === "development" &&
            runtimeConfig.apiUrl.includes("localhost");
          if (shouldSetLoginIdCookie) {
            Cookies.set("loginId", response.loginId, {
              domain: runtimeConfig.cookieDomain,
              path: "/",
            });
          }

          console.log("Account changed to " + response.acctName);
          fetchMenuItems();
          navigate("/home");
        } else {
          console.log("Failed to change account");
        }
      }
    } catch (error) {
      console.log("Failed to change account");
    } finally {
      setIsLoading(false);
      setNewAcctId("");
    }
  };
  /* Collapse the top dropdown navbar when a link is clicked. Without this on a mobile
  screen the dropdown navbar stays in place */
  const collapseNavbarOnClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (
      (target.tagName === "A" &&
        (target.classList.contains("dropdown-item") ||
          target.getAttribute("data-bs-toggle") === "")) ||
      target.innerText === "TrustCall" ||
      target.innerText === "Users"
    ) {
      const navbar = document.getElementById("top-navbar");
      if (navbar) {
        navbar.classList.remove("show");
      }
    }
  };
  if (isLoading) {
    return <Loading />;
  }

  const displayLogo = isDefaultLogo
    ? "/images/DNCScrub-by-Sonera-lockup.svg"
    : logo;

  return (
    <nav className="navbar navbar-expand-md navbar-background">
      <div className="container-fluid">
        {/* Only show the campaign treeview toggle button if in the
        main section  */}
        {location.pathname.includes("/main/") && (
          <button
            className={`${Styles["navbar-toggler-left-sidebar"]}`}
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#left-hand-navbar"
            title="Toggle campaign menu"
          >
            <span
              className={`navbar-toggler-icon ${Styles["navbar-toggler-icon-top-menu"]}`}
            ></span>
          </button>
        )}

        <Link className="" to="/home">
          {displayLogo && (
            <img
              alt={isDefaultLogo ? "DNCScrub by Sonera" : "DNCScrub logo"}
              className={`${Styles["logo"]} account-logo`}
              src={displayLogo}
            />
          )}
        </Link>

        <button
          className={`navbar-toggler ${Styles["navbar-toggler-top-menu"]}`}
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#top-navbar"
          title="Toggle navigation menu"
        >
          <span
            className={`navbar-toggler-icon ${Styles["navbar-toggler-icon-top-menu"]}`}
          ></span>
        </button>

        <div
          className={`collapse navbar-collapse ${Styles["top-navbar-container"]}`}
          id="top-navbar"
          onClick={collapseNavbarOnClick}
        >
          <ul className="navbar-nav me-auto nav-pills justify-content-start flex-grow-1">
            {/* Iterate through all the navbar items to build navbar */}
            {filteredDropdowns.map((dropdown, index) => (
              <li key={index} className="nav-item dropdown">
                <Link
                  aria-current="page"
                  aria-expanded="false"
                  className={`nav-link dropdown-toggle`}
                  data-bs-toggle={dropdown.items.length > 1 ? "dropdown" : ""}
                  to={
                    dropdown.items.length === 1 ? dropdown.items[0].href : "#"
                  }
                  role="button"
                  onClick={
                    dropdown.items.length > 1
                      ? (e) => e.preventDefault()
                      : undefined
                  }
                >
                  <span
                    className={`${underlineSectionIfActive(dropdown.label)}`}
                  >
                    {dropdown.label === "Compliance Guide" ? (
                      <span>
                        Compliance
                        <span className="d-none d-xl-inline"> Guide</span>
                      </span>
                    ) : (
                      dropdown.label
                    )}
                  </span>
                </Link>
                {dropdown.items.length > 1 && (
                  <ul className="dropdown-menu">
                    {dropdown.items.map((item, i) => (
                      <React.Fragment key={i}>
                        <li className="nav-item">
                          {item.external ? (
                            <a className="dropdown-item" href={item.href} target="_blank" rel="noopener noreferrer">
                              <SVGForPage
                                pageKeyword={item.iconKeyword ?? item.href.split("/").pop()}
                              />
                              {item.label}
                            </a>
                          ) : (
                            <Link className="dropdown-item" to={item.href}>
                              <SVGForPage
                                pageKeyword={item.iconKeyword ?? item.href.split("/").pop()}
                              />
                              {item.label}
                            </Link>
                          )}
                        </li>
                      </React.Fragment>
                    ))}
                  </ul>
                )}
              </li>
            ))}
            <a
              className="nav-link d-none d-md-block"
              role="button"
              href={`${runtimeConfig.classicUrl}/app/`}
            >
              <span className="fs-0">
                <i className="bi bi-box-arrow-right"></i> Classic App
              </span>
            </a>
          </ul>

          {/* Start right-hand part of navbar */}
          <div className="d-flex align-items-center gap-0 gap-lg-2 navbar-nav justify-content-end flex-nowrap">
            <HelpButton />
            <ThemeSelector />
            {/* Start sysadmin acct changer */}
            <div>
              {isSysadmin && (
                <>
                  <div className={`d-flex`}>
                    <input
                      type="text"
                      name="acctId"
                      style={{ width: 60 }}
                      className={`${Styles["sysadmin-acctid-switch-box"]}`}
                      onChange={handleAcctChange}
                      value={newAcctId}
                      placeholder="Acct Id"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleAcctIdChange();
                        }
                      }}
                    />
                    <button
                      className="text-white ps-2 pe-2 btn btn-secondary btn-sm rounded-0 sysadmin-go-btn"
                      onClick={handleAcctIdChange}
                    >
                      Go
                    </button>
                  </div>
                  <a
                    href={`${runtimeConfig.classicUrl}/app/sysadmin/accounts.adp?acctId=${acctId}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className={`dropdown-item ${Styles["link-to-sysadmin-current-acct"]}`}
                  >
                    ({acctId})
                  </a>
                </>
              )}
            </div>
            {/* End sysadmin acct changer */}
            <div
              className={`d-none d-lg-flex flex-column justify-content-center ${Styles["sysadmin-sales-links"]}`}
            >
              {isSysadmin && (
                <>
                  {/* For now lets open sysadmin in a new window. This is what our support team is used to
              Sysadmin can also be embedded with url /t/sysadmin but unclear if we will use */}
                  <a
                    href={`${runtimeConfig.classicUrl}/app/sysadmin/`}
                    target="_blank"
                    className="dropdown-item d-flex gap-1"
                    rel="noreferrer noopener"
                  >
                    <i className={`bi bi-gear`}></i>
                    <span
                      className={`d-none ${Styles["d-show-sysadmin-link-title-lg"]} fw-semibold`}
                    >
                      Sysadmin
                    </span>
                  </a>
                </>
              )}

              {hasSalesReportAccess && (
                <Link
                  to="/sales"
                  className="dropdown-item d-flex gap-1"
                >
                  <i className={`bi bi-briefcase`}></i>
                  <span
                    className={`d-none ${Styles["d-show-sysadmin-link-title-lg"]} fw-semibold`}
                  >
                    Sales Dashboard
                  </span>
                </Link>
              )}

              {hasSalesReportAccess && (
                <Link
                  to="/t/sales-production-report"
                  className="dropdown-item d-flex gap-1"
                >
                  <i className={`bi bi-file-earmark-spreadsheet`}></i>
                  <span
                    className={`d-none ${Styles["d-show-sysadmin-link-title-lg"]} fw-semibold`}
                  >
                    Sales Report
                  </span>
                </Link>
              )}
            </div>
            <ProfileDropdown showSalesReport={hasSalesReportAccess ?? false} />
          </div>
          {/* End right-hand part of navbar */}
        </div>
      </div>
    </nav>
  );
};

export default Header;
